import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { Loader2, Copy, ArrowLeft, Cookie, DollarSign, TrendingUp, CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OfficialLinkDetailPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    axios.get(`${API}/official-links/${slug}`)
      .then((r) => {
        setData(r.data);
        const p = r.data.program;
        document.title = `${p.name} Affiliate Link — Official & Verified | Social Flats`;
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
          meta = document.createElement("meta");
          meta.name = "description";
          document.head.appendChild(meta);
        }
        meta.content = `Get the official ${p.name} affiliate link. ${p.commission || ""} commission, ${p.cookie_days || "?"}-day cookie. Verified and live on Social Flats.`;
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const copyPersonal = async () => {
    const link = `${window.location.origin}/go/${user.handle}/${slug}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Your personal tracked link copied — you earn on every click");
    } catch { toast.error("Copy failed"); }
  };

  if (loading) return <main className="max-w-4xl mx-auto px-4 py-24 flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</main>;

  if (notFound || !data) return (
    <main data-testid="official-link-404" className="max-w-4xl mx-auto px-4 py-24 text-center">
      <h1 className="font-heading text-3xl mb-4">Link not available yet</h1>
      <p className="text-slate-500 mb-8">This program isn't live on Social Flats yet — new links go up every week.</p>
      <Link to="/official-links" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-700 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Browse live links
      </Link>
    </main>
  );

  const p = data.program;
  const confirmed = !!(user && user.email_verified && user.handle);

  return (
    <main data-testid="official-link-detail" className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
      <Link to="/official-links" data-testid="back-to-links" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-8">
        <ArrowLeft className="w-4 h-4" /> All official links
      </Link>

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
          <CheckCircle2 className="w-3 h-3" /> {p.status === "live" ? "Live" : "Approved"} · Official
        </span>
        {p.niche_name && <span className="text-xs text-slate-400 uppercase tracking-wider">{p.niche_name}</span>}
      </div>

      <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mb-4">{p.name} affiliate link</h1>
      <p className="text-base text-slate-600 leading-relaxed max-w-2xl mb-8">{p.description || `The official ${p.name} affiliate program, verified and onboarded by Social Flats.`}</p>

      <div className="grid grid-cols-3 gap-3 max-w-lg mb-10">
        <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
          <DollarSign className="w-4 h-4 mx-auto text-emerald-600" />
          <p className="font-semibold text-slate-900 mt-1.5">{p.commission || "—"}</p>
          <p className="text-xs text-slate-400">Commission</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
          <Cookie className="w-4 h-4 mx-auto text-amber-600" />
          <p className="font-semibold text-slate-900 mt-1.5">{p.cookie_days || "—"} days</p>
          <p className="text-xs text-slate-400">Cookie window</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
          <TrendingUp className="w-4 h-4 mx-auto text-indigo-600" />
          <p className="font-semibold text-slate-900 mt-1.5">${(p.epc || 0).toFixed(2)}</p>
          <p className="text-xs text-slate-400">EPC</p>
        </div>
      </div>

      <div className="bg-slate-900 rounded-xl p-8 sm:p-10 mb-10">
        <p className="uppercase text-[11px] tracking-[0.2em] font-semibold text-emerald-400 mb-2">Members-only tracked link</p>
        {confirmed ? (
          <>
            <h2 className="font-heading text-2xl text-white mb-3">Your {p.name} link is ready</h2>
            <p className="text-sm text-slate-300 leading-relaxed mb-6 max-w-xl">Share your personal tracked link — every click is attributed to you and counts toward your tier and payouts. (We never hand out the raw affiliate link — your tracked link is what earns you commission.)</p>
            <button onClick={copyPersonal} data-testid="copy-personal-link"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-emerald-500 text-white text-base font-bold hover:bg-emerald-400 transition-colors">
              <Copy className="w-5 h-5" /> Copy my tracked link
            </button>
          </>
        ) : user ? (
          <>
            <h2 className="font-heading text-2xl text-white mb-3">Confirm your email to unlock this link</h2>
            <p className="text-sm text-slate-300 leading-relaxed mb-6 max-w-xl">Tracked links are reserved for confirmed ambassadors. Check your inbox for the confirmation email — once confirmed, your personal {p.name} link appears right here.</p>
            <Link to="/dashboard" data-testid="detail-verify-cta"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-emerald-500 text-white text-base font-bold hover:bg-emerald-400 transition-colors">
              <ShieldCheck className="w-5 h-5" /> Confirm my email
            </Link>
          </>
        ) : (
          <>
            <h2 className="font-heading text-2xl text-white mb-3">Join now to get your {p.name} link</h2>
            <p className="text-sm text-slate-300 leading-relaxed mb-6 max-w-xl">Tracked links are for confirmed Social Flats ambassadors only. Sign up free, confirm your email, and every click you drive earns you commission.</p>
            <Link to="/signup" data-testid="detail-signup-cta"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-emerald-500 text-white text-base font-bold hover:bg-emerald-400 transition-colors">
              <Sparkles className="w-5 h-5" /> Join now — earn per click
            </Link>
          </>
        )}
      </div>

      <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-5 mb-12">
        <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-emerald-900 leading-relaxed">
          <b>Officially onboarded.</b> Social Flats holds a verified partnership with {p.name} — commissions confirmed, cookie window locked at {p.cookie_days || "?"} days, and payouts handled through your ambassador account.
        </p>
      </div>

      {data.related?.length > 0 && (
        <div>
          <h3 className="font-heading text-xl tracking-tight mb-4">More live links in {p.niche_name}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {data.related.map((r) => (
              <Link key={r.slug} to={`/official-links/${r.slug}`} data-testid={`related-${r.slug}`}
                className="bg-white border border-slate-200 rounded-lg p-4 hover:border-emerald-300 transition-colors">
                <p className="font-semibold text-slate-900 text-sm">{r.name}</p>
                <p className="text-xs text-slate-400 mt-1">{r.commission}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </main>
  );
}
